import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Header } from "@/components/site/Header";
import { Press } from "@/components/site/Press";
import { Footer } from "@/components/site/Footer";
import { usePageSeo } from "@/lib/seo";

export default function Engagements() {
  usePageSeo({
    path: "/engagements",
    title: "Engagements & Media Coverage | Park Communications",
    description:
      "Engagements and earned media coverage from Park Communications — a strategic communications agency and crisis PR firm — across business, culture, entertainment, sports and public affairs.",
  });

  return (
    <main data-testid="engagements-page">
      <Header solid linkBase="/" />

      <section className="relative bg-navy text-ivory grain overflow-hidden pt-40 pb-20">
        <div className="relative mx-auto max-w-7xl px-6">
          <Link to="/" data-testid="engagements-back" className="overline text-ivory/40 hover:text-gold transition-colors">
            &larr; Back to home
          </Link>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="overline text-gold mt-10 mb-6"
          >
            The record
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.03] max-w-4xl"
          >
            Engagements &amp; media coverage.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-ivory/65 text-lg leading-relaxed max-w-2xl mt-8"
          >
            A cross-section of earned media placed for Park Communications clients across business, culture,
            entertainment, sports and public affairs. Active engagements are kept in confidence.
          </motion.p>
        </div>
      </section>

      <section data-testid="engagements-intro" className="bg-ivory pt-24">
        <div className="mx-auto max-w-3xl px-6 space-y-6 text-navy/75 text-lg leading-relaxed">
          <p>
            Earned media is the clearest proof of what a <strong>strategic communications agency</strong> can do. The
            placements below span more than a decade of work — in national newsrooms, business desks, cultural
            coverage, sports and local affairs — and each one began the same way: with a clear strategy, a credible
            message, and a real relationship with the reporter telling the story.
          </p>
          <p>
            Some of this work started as <strong>crisis management help</strong> — a client facing litigation, an
            investigation, or a story that was already moving without them. In those moments, a{" "}
            <strong>crisis PR firm</strong> earns its keep by getting ahead of the narrative: verifying the facts,
            shaping the framing, and making sure the coverage that runs is accurate and fair. Other engagements were
            slower, deliberate campaigns to introduce a founder, reposition a brand, or build the kind of durable
            reputation that pays off years later.
          </p>
          <p>
            We work across beats on purpose. A reputation is rarely built or defended in a single publication, and the
            audiences that matter — investors, regulators, customers, employees, and the public — read widely. Placing
            a story in <em>The New York Times</em>, <em>The Wall Street Journal</em> or <em>Bloomberg</em> carries
            weight with one audience; coverage in <em>Variety</em>, <em>Vox</em>, or a trade outlet reaches another. Our
            job is to understand which rooms a client needs to win, and to earn credible coverage in each of them.
          </p>
          <p>
            The engagements shown here are a representative sample, not a complete client list. Much of our most
            sensitive work is never publicized — that is often the point. If you are weighing whether to{" "}
            <strong>hire a crisis communications consultant</strong>, or you simply want a partner who can help you tell
            a clearer story over time, the record below is a good place to start. When you are ready, we are glad to
            talk in confidence about what your situation actually needs.
          </p>
        </div>
      </section>

      <Press standalone />

      <section className="bg-navy text-ivory grain relative overflow-hidden py-20">
        <div className="relative mx-auto max-w-7xl px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-8">
          <h2 className="font-serif font-light text-3xl sm:text-4xl max-w-xl">Have a story that needs the right strategy?</h2>
          <a
            href="/#contact"
            data-testid="engagements-cta"
            className="shrink-0 inline-flex items-center gap-3 bg-gold hover:bg-ivory text-navy px-8 py-4 overline transition-colors"
          >
            Start a conversation <span>&rarr;</span>
          </a>
        </div>
      </section>

      <Footer />
    </main>
  );
}
